import React from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import dollarsToCents from 'dollars-to-cents';
import history from 'app/routes/history';

import { formatAmount, parseAmount } from 'app/utils/parsers';
import feeTypes from 'app/utils/static/feeTypes';
import timeframes from 'app/utils/static/timeframes';
import feeConfigurations from 'app/utils/static/feeConfigurations';
import matchFeeLineItems from 'app/utils/static/matchFeeLineItems';

import HeaderThin from 'app/components/layout/HeaderThin';
import SectionGroup from 'app/components/general/SectionGroup';
import SchemaForm from 'app/components/general/SchemaForm';

import * as organisationFeeActions from 'app/ducks/organisationFees/actions';
import * as loadingSelectors from 'app/ducks/loading/selectors';
import * as organisationFeeSelectors from 'app/ducks/organisationFees/selectors';
import * as organisationSelectors from 'app/ducks/organisations/selectors';
import * as competitionSelectors from 'app/ducks/competitions/selectors';

const uiSchema = {
  amount: { 'ui:field': 'currency' },
  feeType: {
    'ui:field': 'select',
    classNames: 'col',
    props: { disabled: true },
  },
  timeframe: { 'ui:field': 'select', classNames: 'col' },
  feeConfig: {
    items: {
      amount: { 'ui:field': 'currency' },
      feeConfigType: { 'ui:field': 'select', classNames: 'col' },
      filter: { 'ui:field': 'select', classNames: 'col' },
    },
  },
  metadata: {
    items: {
      amount: { 'ui:field': 'currency' },
      lineItemType: { 'ui:field': 'select', classNames: 'col' },
      feeConfig: {
        items: {
          amount: { 'ui:field': 'currency' },
          feeConfigType: { 'ui:field': 'select', classNames: 'col' },
          filter: { 'ui:field': 'select', classNames: 'col' },
        },
      },
    },
  },
};
interface IFeeConfigItem {
  amount: string;
  quantity?: number;
  feeConfigType: {
    id: string;
    name: string;
  };
  filter: {
    label: string;
    value: string;
  }[];
}

interface IMetadataItem {
  amount: string;
  quantity: number;
  lineItemType: {
    id: string;
    name: string;
  };
  feeConfig?: IFeeConfigItem[];
}

interface IOrganisationFeeDetailProps {
  appReady: boolean;
  actions: any;
  match: any;
  currentOrganisationFee: any;
  currentOrganisationFeeId: string;
  organisationFees: any;
  organisations: any;
  competitions: any;
  errors: any;
}

interface IOrganisationFeeDetailState {
  originalFormData: any;
  formData: any;
  changed: boolean;
  saved: boolean;
  errors: any;
}

const selector = (state): any => ({
  currentOrganisationFee: organisationFeeSelectors.getCurrentFormOrganisationFee(
    state,
  ),
  currentOrganisationFeeId: state.organisationFees.currentOrganisationFeeId,
  organisationFees: organisationFeeSelectors.getOrganisationFees(state),
  organisations: organisationSelectors.getOrganisations(state),
  competitions: competitionSelectors.getCompetitions(state),
  appReady: loadingSelectors.appReady(state),
  errors: {
    ...state.organisationFees.backendErrors,
  },
});

const actionCreators = _.merge(organisationFeeActions);

@connect(selector, (dispatch) => ({
  actions: bindActionCreators(actionCreators, dispatch),
}))
export default class OrganisationFeeDetail extends React.Component<
IOrganisationFeeDetailProps,
IOrganisationFeeDetailState
> {
  static constructEventVariableFormData = (nextProps) => {
    const compiledMetadata = [];
    _.each(nextProps.currentOrganisationFee.metadata?.defaultLineItems, (m) => {
      const newMetadataObject: IMetadataItem = {
        amount: parseAmount(m.amount),
        quantity: m.quantity,
        lineItemType: {
          id: m.code,
          name: m.description,
        },
        feeConfig: [],
      };
      compiledMetadata.push(newMetadataObject);
    });

    _.each(nextProps.currentOrganisationFee.feeConfig, (f) => {
      const filterInfo = f.metadata?.defaultLineItems[Object.keys(f.metadata.defaultLineItems)[0]];
      const newFeeConfigObject: IFeeConfigItem = {
        feeConfigType: _.find(feeConfigurations, {
          id: f.feeConfigType,
        }),
        filter: _.map(f.filter, (a) => ({
          label: a,
          value: a,
        })),
        amount: parseAmount(filterInfo.amount),
        quantity: filterInfo.quantity,
      };
      const metadataItemIndex = _.findIndex(compiledMetadata, (v) => v.lineItemType.id === filterInfo.code);
      // console.log(metadataItemIndex);
      if (metadataItemIndex > -1) {
        compiledMetadata[metadataItemIndex].feeConfig.push(newFeeConfigObject);
      }
    });
    const feeType = _.find(feeTypes, {
      id: nextProps.currentOrganisationFee.feeType,
    });
    const timeframe = _.find(timeframes, {
      id: nextProps.currentOrganisationFee.timeframe,
    });
    const formData = {
      name: nextProps.currentOrganisationFee.name || '',
      amount: parseAmount(nextProps.currentOrganisationFee.amount) || '',
      feeType: feeType || null,
      timeframe: timeframe || null,
      metadata: compiledMetadata || {},
    };
    return formData;
  }

  static constructEventFormData = (nextProps) => {
    const compiledFeeConfig = [];
    _.each(nextProps.currentOrganisationFee.feeConfig, (f) => {
      const newFeeConfigObject: IFeeConfigItem = {
        feeConfigType: _.find(feeConfigurations, {
          id: f.feeConfigType,
        }),
        filter: _.map(f.filter, (a) => ({
          label: a,
          value: a,
        })),
        amount: parseAmount(f.amount),
      };
      compiledFeeConfig.push(newFeeConfigObject);
    });
    const feeType = _.find(feeTypes, {
      id: nextProps.currentOrganisationFee.feeType,
    });
    const timeframe = _.find(timeframes, {
      id: nextProps.currentOrganisationFee.timeframe,
    });
    const formData = {
      name: nextProps.currentOrganisationFee.name || '',
      amount: parseAmount(nextProps.currentOrganisationFee.amount) || '',
      feeType: feeType || null,
      timeframe: timeframe || null,
      feeConfig: compiledFeeConfig || {},
    };
    return formData;
  }

  static constructNormalFormData = (nextProps) => {
    const feeType = _.find(feeTypes, {
      id: nextProps.currentOrganisationFee.feeType,
    });
    const timeframe = _.find(timeframes, {
      id: nextProps.currentOrganisationFee.timeframe,
    });

    const formData = {
      name: nextProps.currentOrganisationFee.name || '',
      amount: parseAmount(nextProps.currentOrganisationFee.amount) || '',
      feeType: feeType || null,
      timeframe: timeframe || null,
    };
    return formData;
  }

  static defaultProps: IOrganisationFeeDetailProps = {
    appReady: false,
    actions: undefined,
    match: undefined,
    currentOrganisationFee: {},
    currentOrganisationFeeId: undefined,
    organisationFees: undefined,
    organisations: undefined,
    competitions: undefined,
    errors: undefined,
  };

  state: IOrganisationFeeDetailState = {
    originalFormData: {},
    formData: {},
    changed: false,
    saved: false,
    errors: {},
  };

  componentDidMount(): void {
    this.props.actions.getOrganisationFee(
      this.props.match.params.organisationFeeId,
    );
  }

  static getDerivedStateFromProps(nextProps, prevState): any {
    let formData = {};

    if (!prevState.changed) {
      if (nextProps.currentOrganisationFee.feeType === 'event-variable') {
        formData = OrganisationFeeDetail.constructEventVariableFormData(nextProps);
      } else if (nextProps.currentOrganisationFee.feeType === 'event') {
        formData = OrganisationFeeDetail.constructEventFormData(nextProps);
      } else {
        formData = OrganisationFeeDetail.constructNormalFormData(nextProps);
      }
      return {
        ...prevState,
        formData,
        originalFormData: formData,
        errors: nextProps.errors,
      };
    }
    return null;
  }

  isValid = (): boolean => _.isEmpty(this.state.errors) && this.state.changed;

  handleChange = (e): void => {
    const changed = this.state.originalFormData !== e.formData;
    this.setState(() => ({
      formData: e.formData,
      errors: [],
      changed,
    }));
  };

  handleError = (errors): void => {
    this.setState({ errors });
  }

  handleDelete = (e): void => {
    // e.preventDefault();
    const payload = {
      id: this.props.match.params.organisationFeeId,
      status: 'inactive',
    };

    this.props.actions.updateOrganisationFee(payload);
    this.setState({ changed: false });
    history.push(
      `/competitions/${this.props.competitions.currentCompetitionId}`,
    );
  };

  constructSubmitPayload = () => {
    let compiledMetadata;
    let compiledAmount;
    let compiledFeeConfig;
    if (this.state.formData.feeType?.id === 'event') {
      compiledFeeConfig = _.map(this.state.formData.feeConfig, (f) => {
        if (!_.isEmpty(f.feeConfigType?.id) && !_.isEmpty(f.amount) && !_.isEmpty(f.filter)) {
          return {
            feeConfigType: f.feeConfigType?.id,
            amount: dollarsToCents(f.amount),
            feeType: 'static',
            feeMetadata: {},
            filter: _.map(f.filter, (g) => g.value),
          };
        }
      });
      compiledAmount = dollarsToCents(this.state.formData.amount);
    } else if (this.state.formData.feeType?.id === 'walkover' || this.state.formData.feeType?.id === 'walkover-covid') {
      compiledAmount = dollarsToCents(this.state.formData.amount);
    } else if (this.state.formData.feeType?.id === 'event-variable') {
      compiledMetadata = {
        lineItems: {},
        defaultLineItems: {},
      };

      _.each(this.state.formData.metadata, (m) => {
        compiledMetadata.lineItems[m.lineItemType?.id] = {
          code: m.lineItemType?.id,
          description: m.lineItemType?.name,
          amount: dollarsToCents(m.amount),
        };
        compiledMetadata.defaultLineItems[m.lineItemType?.id] = {
          code: m.lineItemType?.id,
          description: m.lineItemType?.name,
          amount: dollarsToCents(m.amount),
          quantity: m.quantity,
        };
      });
      compiledFeeConfig = _.flatMap(this.state.formData.metadata, (m) => _.map(m.feeConfig, (f) => {
        if (!_.isEmpty(f.feeConfigType?.id) && !_.isEmpty(f.amount) && !_.isEmpty(f.filter)) {
          const compiledFeeConfigMetadata = {
            lineItems: {},
            defaultLineItems: {},
          };
          compiledFeeConfigMetadata.lineItems[m.lineItemType?.id] = {
            code: m.lineItemType?.id,
            description: m.lineItemType?.name,
            amount: dollarsToCents(f.amount),
          };
          compiledFeeConfigMetadata.defaultLineItems[m.lineItemType?.id] = {
            code: m.lineItemType?.id,
            description: m.lineItemType?.name,
            amount: dollarsToCents(f.amount),
            quantity: f.quantity,
          };
          return {
            feeConfigType: f.feeConfigType?.id,
            amount: dollarsToCents(f.amount),
            feeType: 'variable',
            metadata: compiledFeeConfigMetadata,
            filter: _.map(f.filter, (g) => g.value),
          };
        }
      }));
    }


    const payload = {
      id: this.props.currentOrganisationFee.id,
      to: 'team-manager',
      name: this.state.formData.name,
      amount: compiledAmount,
      feeType: !_.isEmpty(this.state.formData.feeType)
        ? this.state.formData.feeType.id
        : null,
      metadata: compiledMetadata,
      feeConfig: compiledFeeConfig,
      timeframe: !_.isEmpty(this.state.formData.timeframe) && _.isNumber(this.state.formData.timeframe.id)
        ? this.state.formData.timeframe.id
        : null,
      competition: {
        id: this.props.competitions.currentCompetitionId,
      },
      organisation: {
        id: this.props.organisations.currentOrganisationId,
      },
    };

    return payload;
  }

  handleSubmit = (e): void => {
    if (this.isValid()) {
      const payload = this.constructSubmitPayload();

      this.props.actions.updateOrganisationFee(payload);
    }
    this.setState({ changed: false });
  };

  render(): React.ReactNode {
    // console.log(this.constructSubmitPayload());
    const formSchema = {
      required: ['name', 'feeType', 'amount'],
      properties: {
        name: {
          type: 'string',
          title: 'Name',
        },
        feeType: {
          type: 'object',
          title: 'Fee Type',
          options: feeTypes,
          inputType: 'select',
        },
        amount: {
          type: 'string',
          title: 'Amount',
          inputType: 'currency',
        },
      },
    };

    if (this.state.formData.feeType?.id === 'walkover') {
      formSchema.properties.timeframe = {
        type: ['object', 'null'],
        title: 'Timeframe',
        options: timeframes,
        inputType: 'select',
        clearable: true,
      };
    }

    if (this.state.formData.feeType?.id === 'event') {
      formSchema.required = ['name', 'feeType', 'amount'];
      formSchema.properties.feeConfig = {
        type: 'array',
        title: 'Additional Configurations (Optional)',
        description: 'Create specific match fees, depending on the age group, section or team name.',
        items: {
          properties: {
            feeConfigType: {
              type: ['object', 'null'],
              title: 'Type',
              options: feeConfigurations,
              inputType: 'select',
              clearable: true,
            },
            amount: {
              type: 'string',
              title: 'Amount',
              inputType: 'currency',
            },
            filter: {
              type: 'array',
              title: 'Filter',
              inputType: 'select',
              createable: true,
              isMulti: true,
            },
          },
        },
      };
    }

    if (this.state.formData.feeType?.id === 'event-variable') {
      formSchema.required = ['name', 'feeType'];
      delete formSchema.properties.amount;
      formSchema.properties.metadata = {
        type: 'array',
        title: 'Line Items',
        description: 'Set up the line items for this match fee.',
        items: {
          properties: {
            lineItemType: {
              type: ['object', 'null'],
              title: 'Type',
              options: matchFeeLineItems,
              inputType: 'select',
              clearable: true,
              createable: true,
            },
            amount: {
              type: 'string',
              title: 'Amount (per)',
              inputType: 'currency',
            },
            quantity: {
              type: 'number',
              inputType: 'number',
              title: 'Minimum Quantity',
            },
            feeConfig: {
              type: 'array',
              title: 'Additional Configurations (Optional)',
              description: 'Create specific match fees, depending on the age group, section or team name.',
              items: {
                properties: {
                  feeConfigType: {
                    type: ['object', 'null'],
                    title: 'Type',
                    options: feeConfigurations,
                    inputType: 'select',
                    clearable: true,
                  },
                  amount: {
                    type: 'string',
                    title: 'Amount',
                    inputType: 'currency',
                  },
                  quantity: {
                    type: 'number',
                    inputType: 'number',
                    title: 'Minimum Quantity',
                  },
                  filter: {
                    type: 'array',
                    title: 'Filter',
                    inputType: 'select',
                    createable: true,
                    isMulti: true,
                  },
                },
              },
            },
          },
        },
      };
    }

    return (
      <div>
        <div className="row">
          <div className="col">
            <HeaderThin
              title={this.props.currentOrganisationFee.name}
              divider
            />
          </div>
        </div>
        <div className="row">
          <div className="col col-xl-6">
            <SectionGroup title="Fee Details">
              <SchemaForm
                uiSchema={uiSchema}
                schema={formSchema}
                handleChange={this.handleChange}
                handleError={this.handleError}
                handleSubmit={this.handleSubmit}
                handleDelete={this.handleDelete}
                formData={this.state.formData}
                isValid={this.isValid()}
                isLoading={this.props.organisationFees.isSaving}
                hasSaved={this.props.organisationFees.hasSaved}
                hasFailed={this.props.organisationFees.hasFailed}
                originalFormData={this.state.originalFormData}
              />
            </SectionGroup>
          </div>
        </div>
      </div>
    );
  }
}
